/**
 * Currency Display Component
 * 
 * Formats and displays currency values with consistent styling.
 */
import { cn } from '@/lib/utils';

interface CurrencyDisplayProps {
  value: number;
  currency?: string;
  locale?: string;
  className?: string;
  showSign?: boolean;
  colorize?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

const sizeClasses = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-2xl font-semibold',
};

export function CurrencyDisplay({
  value,
  currency = 'BRL',
  locale = 'pt-BR',
  className,
  showSign = false,
  colorize = false,
  size = 'md',
}: Readonly<CurrencyDisplayProps>) {
  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(value));

  // Always show negative sign, only show positive sign if showSign is true
  const obterSinal = () => {
    if (value < 0) return '-';
    if (showSign && value > 0) return '+';
    return '';
  };
  const sign = obterSinal();
  const displayValue = `${sign}${formatted}`.trim();

  const obterClasseDeCor = () => {
    if (!colorize) return '';
    if (value > 0) return 'text-emerald-600 dark:text-emerald-400';
    if (value < 0) return 'text-rose-600 dark:text-rose-400';
    return 'text-muted-foreground';
  };
  const colorClass = obterClasseDeCor();

  return (
    <span className={cn(sizeClasses[size], colorClass, className)}>
      {displayValue}
    </span>
  );
}

export default CurrencyDisplay;


